/**
 * @file JobRowSkeleton.tsx
 *
 * @description Provides a loading skeleton placeholder for pending job rows in
 * the home page view.
 *
 * @module Components/Skeletons
 * @requires Material-UI
 * @requires React
 */

import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Skeleton,
} from "@mui/material";
import Grid from "@mui/material/Grid";

interface JobRowSkeletonProps {
  reportName?: string;
}

/**
 * @function JobRowSkeleton
 *
 * @description This component creates a job placeholder UI element that mimics the structure of
 * a card in the home page view of reports.
 *
 * @returns {JSX.Element} A skeleton placeholder UI for a card in the home page view.
 */
export const JobRowSkeleton: React.FC<JobRowSkeletonProps> = ({
  reportName,
}) => {
  return (
    <Card variant="outlined" sx={{ backgroundColor: "background.paper" }}>
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Grid container spacing={2} alignItems="center">
          {/* Main Info Section */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Skeleton variant="circular" width={24} height={24} />
              <Typography
                sx={{
                  fontWeight: "bold",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: {
                    xs: "200px",
                    sm: "300px",
                    md: "350px",
                    lg: "400px",
                  },
                  whiteSpace: "nowrap",
                }}
              >
                {reportName}
              </Typography>
            </Box>
            <Skeleton variant="text" width="90%" height={40} />
          </Grid>

          {/* Reported By Section */}
          <Grid size={{ xs: 6, md: 3 }}>
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="text" width="80%" />
          </Grid>

          {/* Modified By Section */}
          <Grid size={{ xs: 6, md: 3 }}>
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="text" width="80%" />
          </Grid>

          {/* Actions Section */}
          <Grid size={{ xs: 12, md: 1 }}>
            <Box display="flex" gap={1} justifyContent="flex-end">
              <Skeleton variant="circular" width={32} height={32} />
              <Skeleton variant="circular" width={32} height={32} />
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
